import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAlertes, invalidateChantier } from '@/lib/queryInvalidation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, CheckCircle2, FileText, Clock, Receipt, Coins, ArrowLeft, Plus, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useContratsST } from "@/hooks/useContratsST";
import { useClosureStore } from "@/stores/closureStore";
import ChantierBilan from "./ChantierBilan";
import ExpensesManager from "./ExpensesManager";
import InvoiceManager from "./InvoiceManager";
import { TerminerStepJoursFuturs, type JourFuturInfo, type JoursFutursBlocInfo } from "@/components/chantiers/TerminerStepJoursFuturs";
import { TerminerStepConfirmSuppression, type ComparaisonFinanciere } from "@/components/chantiers/TerminerStepConfirmSuppression";
import { HeuresSupRecap } from "@/components/heures-sup";
import { useHeuresSupSettings } from "@/hooks/useHeuresSup";
import { usePaiementsChantier } from "@/hooks/usePaiementsChantier";
import { PaiementFormContent } from "./PaiementFormContent";
import type { PaiementFormData } from "@/types/paiement";

interface ChantierClosureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chantierId: string;
  chantierData: any;
  metrics: any;
  membres: any[];
  affectations: any[];
  devis: any[];
  factures: any[];
  frais: any[];
  onClosed: () => void;
}

type DialogStep = 'jours_futurs' | 'confirm_suppression' | 'checklist' | 'add-delay' | 'add-invoice' | 'add-expense' | 'add-payment' | 'bilan';

// Générer les dates ouvrées entre deux dates
function genererJoursOuvres(dateDebut: string, dateFin: string): string[] {
  const jours: string[] = [];
  const debut = new Date(dateDebut);
  const fin = new Date(dateFin);
  const current = new Date(debut);

  while (current <= fin) {
    const dayOfWeek = current.getDay();
    // Exclure samedi (6) et dimanche (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      jours.push(current.toISOString().split('T')[0]);
    }
    current.setDate(current.getDate() + 1);
  }
  return jours;
}

// Fonction helper pour calculer un bloc de jours futurs (initial ou retard)
function calculerBlocJoursFuturs(
  affectationsBloc: any[],
  aujourdhui: string
): JoursFutursBlocInfo | null {
  if (affectationsBloc.length === 0) return null;

  let coutTotal = 0;
  let dateFinPlanifiee = aujourdhui;
  let joursHommeTotal = 0;
  const detailParMembre: JoursFutursBlocInfo['detailParMembre'] = [];

  // Map pour compter les créneaux calendaires uniques (clé: "date:period")
  // On compte les créneaux uniques, pas multiplié par le nombre de membres
  const creneauxUniquesMap = new Map<string, number>(); // valeur = jours (0.5 ou 1)

  // Set pour compter les ouvriers uniques mobilisés
  const ouvriersUniques = new Set<string>();

  affectationsBloc.forEach(aff => {
    const tauxHoraire = aff.taux_horaire_specifique || aff.membre?.taux_horaire || 20;
    const coef = aff.membre?.coefficient_charges || 1.85;
    const heuresParJour = aff.heures_par_jour || 8;

    let joursMembreFuturs = 0;
    let coutMembreFuturs = 0;

    // Si jours_details existe et n'est pas vide, l'utiliser
    if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
      aff.jours_details.forEach((jd: any) => {
        // Jours strictement après aujourd'hui
        if (jd.date > aujourdhui) {
          // value est 0.5 pour AM ou PM, 1 pour FULL
          const jourValue = jd.value || (jd.period === 'FULL' ? 1 : 0.5);
          joursMembreFuturs += jourValue;
          const heures = jourValue < 1 ? heuresParJour / 2 : heuresParJour;
          coutMembreFuturs += heures * tauxHoraire * coef;

          // Enregistrer ce créneau unique (pour le temps calendaire)
          // Pour FULL, on compte 2 créneaux (AM + PM)
          if (jd.period === 'FULL') {
            const keyAM = `${jd.date}:AM`;
            const keyPM = `${jd.date}:PM`;
            if (!creneauxUniquesMap.has(keyAM)) creneauxUniquesMap.set(keyAM, 0.5);
            if (!creneauxUniquesMap.has(keyPM)) creneauxUniquesMap.set(keyPM, 0.5);
          } else {
            const key = `${jd.date}:${jd.period}`;
            if (!creneauxUniquesMap.has(key)) creneauxUniquesMap.set(key, 0.5);
          }

          // Mettre à jour la date de fin planifiée
          if (jd.date > dateFinPlanifiee) {
            dateFinPlanifiee = jd.date;
          }
        }
      });
    } else if (aff.date_debut && aff.date_fin) {
      // Fallback: générer les jours à partir de date_debut et date_fin
      const joursGeneres = genererJoursOuvres(aff.date_debut, aff.date_fin);
      joursGeneres.forEach(dateStr => {
        if (dateStr > aujourdhui) {
          joursMembreFuturs += 1;
          coutMembreFuturs += heuresParJour * tauxHoraire * coef;

          // Enregistrer les créneaux uniques (FULL = AM + PM)
          const keyAM = `${dateStr}:AM`;
          const keyPM = `${dateStr}:PM`;
          if (!creneauxUniquesMap.has(keyAM)) creneauxUniquesMap.set(keyAM, 0.5);
          if (!creneauxUniquesMap.has(keyPM)) creneauxUniquesMap.set(keyPM, 0.5);

          // Mettre à jour la date de fin planifiée
          if (dateStr > dateFinPlanifiee) {
            dateFinPlanifiee = dateStr;
          }
        }
      });
    }

    if (joursMembreFuturs > 0) {
      coutTotal += coutMembreFuturs;
      joursHommeTotal += joursMembreFuturs;
      ouvriersUniques.add(aff.membre?.id || aff.membre_equipe_id || aff.id);
      detailParMembre.push({
        membreId: aff.membre?.id || aff.membre_equipe_id || aff.id,
        prenom: aff.membre?.prenom || 'Membre',
        nom: aff.membre?.nom || '',
        jours: joursMembreFuturs,
        cout: Math.round(coutMembreFuturs * 100) / 100,
      });
    }
  });

  if (joursHommeTotal === 0) return null;

  // Calculer les créneaux calendaires et jours calendaires
  const creneauxCalendaires = creneauxUniquesMap.size;
  // Jours calendaires = somme des valeurs (chaque créneau vaut 0.5 jour)
  const joursCalendaires = Array.from(creneauxUniquesMap.values()).reduce((sum, v) => sum + v, 0);

  return {
    creneauxCalendaires,
    joursCalendaires,
    nbOuvriers: ouvriersUniques.size,
    coutTotal: Math.round(coutTotal * 100) / 100,
    joursHommeTotal,
    dateFinPlanifiee,
    detailParMembre,
  };
}

// Fonction pour trouver la date du dernier jour travaillé
function getLastWorkedDate(affectations: any[]): string {
  let lastDate = new Date().toISOString().split('T')[0]; // Fallback: aujourd'hui

  affectations.forEach((aff) => {
    // Si l'affectation a jours_details, chercher la date max
    if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
      aff.jours_details.forEach((jd: any) => {
        if (jd.date > lastDate) {
          lastDate = jd.date;
        }
      });
    } else if (aff.date_fin) {
      // Sinon utiliser date_fin de l'affectation
      const affDateFin = aff.date_fin.split('T')[0];
      if (affDateFin > lastDate) {
        lastDate = affDateFin;
      }
    }
  });

  return lastDate;
}

const ChantierClosureDialog = ({
  open,
  onOpenChange,
  chantierId,
  chantierData,
  metrics,
  membres,
  affectations,
  devis,
  factures,
  frais,
  onClosed,
}: ChantierClosureDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startClosure } = useClosureStore();

  // Hook pour les paramètres heures supplémentaires
  const entrepriseId = chantierData?.entreprise_id;
  const { settings: heuresSupSettings } = useHeuresSupSettings(entrepriseId);

  // Hook centralisé paiements : totals, addPaiement, facturesClients
  const { addPaiement, facturesClients, totals: paiementTotals } = usePaiementsChantier(chantierId);

  // Données contrats sous-traitant pour le warning de clôture
  const { totalResteAFacturer, contrats: contratsST } = useContratsST(chantierId);

  // Membres affectés uniques pour les heures supplémentaires
  const membresAffectes = useMemo(() => {
    const uniqueMembreIds = [...new Set(affectations.map((aff: any) => aff.membre_equipe_id))];
    return uniqueMembreIds
      .map((membreId: string) => membres.find((m: any) => m.id === membreId))
      .filter(Boolean)
      .map((m: any) => ({
        id: m.id,
        nom: m.nom,
        prenom: m.prenom,
        taux_horaire: m.taux_horaire || 0,
      }));
  }, [affectations, membres]);

  // Déterminer le step initial en fonction des jours futurs
  const [step, setStep] = useState<DialogStep>('jours_futurs');
  const [isSupprimantJours, setIsSupprimantJours] = useState(false);
  // État pour savoir si les affectations avec membres ont été chargées
  const [affectationsLoaded, setAffectationsLoaded] = useState(false);

  // Date de fin réelle = dernier jour travaillé (basé sur les affectations)
  const [dateFinReelle, setDateFinReelle] = useState(() => getLastWorkedDate(affectations));
  const [loading, setLoading] = useState(false);

  // États locaux pour factures, frais et metrics (chargés dynamiquement)
  const [localFactures, setLocalFactures] = useState<any[]>(factures);
  const [localFrais, setLocalFrais] = useState<any[]>(frais);
  const [localMetrics, setLocalMetrics] = useState<any>(metrics);
  // Affectations locales (peuvent être modifiées après suppression des jours futurs)
  const [localAffectations, setLocalAffectations] = useState<any[]>(affectations);

  // Fix B2 : Guard pour ne sync les données du parent que quand le dialog VIENT de s'ouvrir
  // Empêche le reset du state local quand les props changent en arrière-plan
  // (causé par Realtime reconnexion après tab switch)
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Dialog vient de s'ouvrir → sync initial des données
      setLocalFactures(factures);
      setLocalFrais(frais);
      setLocalMetrics(metrics);
      wasOpenRef.current = true;
    }
    if (!open) {
      wasOpenRef.current = false;
    }
  }, [open, factures, frais, metrics]);

  // Mettre à jour la date de fin quand les affectations changent
  useEffect(() => {
    if (localAffectations.length > 0) {
      setDateFinReelle(getLastWorkedDate(localAffectations));
    }
  }, [localAffectations]);

  // Calculer les jours futurs (après aujourd'hui) - séparation initial/retard
  const joursFutursInfo = useMemo<JourFuturInfo | null>(() => {
    if (!localAffectations || localAffectations.length === 0) return null;

    const aujourdhui = new Date().toISOString().split('T')[0];

    // Séparer les affectations initiales, de retard et de reprise
    const affectationsInitiales = localAffectations.filter(
      aff => !aff.notes || (!aff.notes.startsWith('Retard chantier') && aff.notes !== 'Reprise chantier')
    );
    const affectationsRetard = localAffectations.filter(
      aff => aff.notes && aff.notes.startsWith('Retard chantier')
    );
    const affectationsReprise = localAffectations.filter(
      aff => aff.notes === 'Reprise chantier'
    );

    // Calculer les blocs
    const blocInitial = calculerBlocJoursFuturs(affectationsInitiales, aujourdhui);
    const blocRetard = calculerBlocJoursFuturs(affectationsRetard, aujourdhui);
    const blocReprise = calculerBlocJoursFuturs(affectationsReprise, aujourdhui);

    // Si aucun bloc n'a de jours, retourner null
    if (!blocInitial && !blocRetard && !blocReprise) return null;

    // Calculer les totaux combinés
    const totalJours = (blocInitial?.joursHommeTotal || 0) + (blocRetard?.joursHommeTotal || 0) + (blocReprise?.joursHommeTotal || 0);
    const totalCout = (blocInitial?.coutTotal || 0) + (blocRetard?.coutTotal || 0) + (blocReprise?.coutTotal || 0);
    const dateFinPlanifiee = [
      blocInitial?.dateFinPlanifiee || '',
      blocRetard?.dateFinPlanifiee || '',
      blocReprise?.dateFinPlanifiee || '',
    ].sort().pop() || aujourdhui;

    // Combiner les détails par membre pour compatibilité
    const detailParMembre: JourFuturInfo['detailParMembre'] = [];
    const membreMap = new Map<string, JourFuturInfo['detailParMembre'][0]>();

    // Ajouter les membres du bloc initial
    blocInitial?.detailParMembre.forEach(m => {
      const existing = membreMap.get(m.membreId);
      if (existing) {
        existing.jours += m.jours;
        existing.cout += m.cout;
        existing.nbDemiJournees += Math.ceil(m.jours * 2); // Approximation
      } else {
        membreMap.set(m.membreId, {
          ...m,
          nbDemiJournees: Math.ceil(m.jours * 2),
        });
      }
    });

    // Ajouter les membres du bloc retard
    blocRetard?.detailParMembre.forEach(m => {
      const existing = membreMap.get(m.membreId);
      if (existing) {
        existing.jours += m.jours;
        existing.cout += m.cout;
        existing.nbDemiJournees += Math.ceil(m.jours * 2);
      } else {
        membreMap.set(m.membreId, {
          ...m,
          nbDemiJournees: Math.ceil(m.jours * 2),
        });
      }
    });

    // Ajouter les membres du bloc reprise
    blocReprise?.detailParMembre.forEach(m => {
      const existing = membreMap.get(m.membreId);
      if (existing) {
        existing.jours += m.jours;
        existing.cout += m.cout;
        existing.nbDemiJournees += Math.ceil(m.jours * 2);
      } else {
        membreMap.set(m.membreId, {
          ...m,
          nbDemiJournees: Math.ceil(m.jours * 2),
        });
      }
    });

    membreMap.forEach(m => detailParMembre.push(m));

    const totalDemiJournees = detailParMembre.reduce((sum, m) => sum + m.nbDemiJournees, 0);

    // Calculer les jours calendaires combinés
    // Les blocs initial et retard sont sur des périodes distinctes (travail normal vs retard)
    // On additionne les jours calendaires car ils ne se chevauchent pas
    const totalJoursCalendaires =
      (blocInitial?.joursCalendaires || 0) + (blocRetard?.joursCalendaires || 0) + (blocReprise?.joursCalendaires || 0);

    // Nombre d'ouvriers uniques = union des ouvriers des deux blocs
    // On utilise les membres détaillés pour compter les uniques
    const totalNbOuvriers = detailParMembre.length;

    return {
      initial: blocInitial,
      retard: blocRetard,
      reprise: blocReprise,
      totalJours,
      totalCout: Math.round(totalCout * 100) / 100,
      dateFinPlanifiee,
      nbDemiJournees: totalDemiJournees,
      totalJoursCalendaires,
      totalNbOuvriers,
      detailParMembre,
    };
  }, [localAffectations]);

  // Calculer la comparaison financière pour la confirmation de suppression
  const comparaisonFinanciere = useMemo<ComparaisonFinanciere | null>(() => {
    if (!joursFutursInfo || !localMetrics) return null;

    const budgetHT = localMetrics.budget_ht || 0;
    // cout_main_oeuvre_reel inclut les affectations normales + retard (tout le travail planifié)
    const coutMOActuel = localMetrics.cout_main_oeuvre_reel || 0;

    // Utiliser marge_finale et marge_finale_pct qui incluent TOUS les coûts (y compris retard)
    // C'est la vraie rentabilité finale estimée du chantier
    const margeActuelle = localMetrics.marge_finale || 0;
    const rentaActuelle = localMetrics.marge_finale_pct || 0;

    // Coût total actuel = Budget - Marge finale (inclut tout: MO, fixes, pénalités, retard)
    const coutTotalActuel = budgetHT - margeActuelle;

    // Après suppression: on retire le coût des jours futurs
    const coutASupprimer = joursFutursInfo.totalCout;
    const coutMOApres = coutMOActuel - coutASupprimer;
    const coutTotalApres = coutTotalActuel - coutASupprimer;

    // La marge augmente du montant des coûts supprimés
    const margeApres = margeActuelle + coutASupprimer;
    const rentaApres = budgetHT > 0 ? (margeApres / budgetHT) * 100 : 0;

    return {
      coutMOAvant: coutMOActuel,
      coutTotalAvant: coutTotalActuel,
      margeAvant: margeActuelle,
      rentaAvant: rentaActuelle,
      coutMOApres,
      coutTotalApres,
      margeApres,
      rentaApres,
      coutASupprimer,
      joursASupprimer: joursFutursInfo.totalJours,
      // Affichage ergonomique (temps calendaire + ouvriers)
      joursCalendaires: joursFutursInfo.totalJoursCalendaires,
      nbOuvriers: joursFutursInfo.totalNbOuvriers,
    };
  }, [joursFutursInfo, localMetrics]);

  // Déterminer s'il y a des jours futurs
  const hasJoursFuturs = joursFutursInfo && joursFutursInfo.totalJours > 0;

  // Charger les affectations avec les détails membres à l'ouverture
  useEffect(() => {
    if (open && chantierId) {
      setAffectationsLoaded(false);
      // Recharger les affectations avec la jointure sur membres_equipe pour avoir les noms
      const loadAffectationsWithMembres = async () => {
        const { data } = await supabase
          .from("affectations_chantiers")
          .select(`
            *,
            membre:membres_equipe(id, nom, prenom, taux_horaire, coefficient_charges, heures_par_jour)
          `)
          .eq("chantier_id", chantierId);
        if (data) {
          setLocalAffectations(data);
        }
        setAffectationsLoaded(true);
      };
      loadAffectationsWithMembres();
    } else if (!open) {
      // Reset quand le dialog se ferme
      setAffectationsLoaded(false);
    }
  }, [open, chantierId]);

  // Reset step à l'ouverture du dialog (attendre que les affectations soient chargées)
  // Fix B2 : stepInitRef empêche le reset du step quand hasJoursFuturs change en arrière-plan
  const stepInitRef = useRef(false);
  useEffect(() => {
    if (open && affectationsLoaded && !stepInitRef.current) {
      // Dialog vient de s'ouvrir ET affectations chargées → définir le step initial
      setStep(hasJoursFuturs ? 'jours_futurs' : 'checklist');
      stepInitRef.current = true;
    }
    if (!open) {
      stepInitRef.current = false;
    }
  }, [open, affectationsLoaded, hasJoursFuturs]);

  // Si pas de jours futurs et qu'on est sur jours_futurs, passer à checklist
  useEffect(() => {
    if (step === 'jours_futurs' && !hasJoursFuturs) {
      setStep('checklist');
    }
  }, [step, hasJoursFuturs]);

  // Recharger les factures depuis la BDD
  const reloadFactures = async () => {
    const { data } = await supabase
      .from("factures_fournisseurs")
      .select("*")
      .eq("chantier_id", chantierId);
    if (data) setLocalFactures(data);
  };

  // Recharger les frais depuis la BDD
  const reloadFrais = async () => {
    const { data } = await supabase
      .from("frais_chantier")
      .select("*")
      .eq("chantier_id", chantierId);
    if (data) setLocalFrais(data);
  };

  // Recharger les metrics depuis la vue realtime (après ajout de coûts)
  const reloadMetrics = async () => {
    const { data } = await supabase
      .from("chantier_metrics_realtime")
      .select("metrics")
      .eq("chantier_id", chantierId)
      .single();
    if (data?.metrics) {
      setLocalMetrics(data.metrics);
    }
  };

  // Recharger les affectations depuis la BDD
  const reloadAffectations = async () => {
    const { data } = await supabase
      .from("affectations_chantiers")
      .select(`
        *,
        membre:membres_equipe(id, nom, prenom, taux_horaire, coefficient_charges, heures_par_jour)
      `)
      .eq("chantier_id", chantierId);
    if (data) setLocalAffectations(data);
  };

  // Handler: l'utilisateur veut maintenir les jours futurs → passer à checklist
  const handleMaintenirJoursFuturs = () => {
    setStep('checklist');
  };

  // Handler: l'utilisateur veut retirer les jours futurs → montrer confirmation
  const handleRetirerJoursFuturs = () => {
    setStep('confirm_suppression');
  };

  // Handler: annuler la suppression → retour à jours_futurs
  const handleAnnulerSuppression = () => {
    setStep('jours_futurs');
  };

  // Handler: confirmer la suppression des jours futurs
  const supprimerJoursFuturs = async () => {
    if (!joursFutursInfo || joursFutursInfo.totalJours === 0) {
      setStep('checklist');
      return;
    }

    setIsSupprimantJours(true);
    try {
      const aujourdhui = new Date().toISOString().split('T')[0];

      // Pour chaque affectation, supprimer les jours futurs
      for (const aff of localAffectations) {
        if (!aff.jours_details || !Array.isArray(aff.jours_details)) continue;

        // Filtrer pour ne garder que les jours <= aujourd'hui
        const joursGardes = aff.jours_details.filter((jd: any) => jd.date <= aujourdhui);

        // Calculer le nouveau jours_travailles
        const nouveauJoursTravailles = joursGardes.reduce((sum: number, jd: any) => sum + (jd.value || 1), 0);

        if (joursGardes.length === 0) {
          // Supprimer l'affectation si plus aucun jour
          await supabase
            .from("affectations_chantiers")
            .delete()
            .eq("id", aff.id);
        } else {
          // Mettre à jour l'affectation avec les jours restants
          const nouvelleDateFin = joursGardes.length > 0
            ? joursGardes[joursGardes.length - 1].date
            : aujourdhui;

          // Préparer les données de mise à jour
          const updateData: any = {
            jours_details: joursGardes,
            jours_travailles: nouveauJoursTravailles,
            date_fin: nouvelleDateFin,
          };

          // Si c'est une affectation de retard, mettre à jour la note avec le nouveau nombre de jours
          if (aff.notes && aff.notes.startsWith('Retard chantier')) {
            updateData.notes = `Retard chantier - ${nouveauJoursTravailles} jour${nouveauJoursTravailles > 1 ? 's' : ''}`;
          }

          await supabase
            .from("affectations_chantiers")
            .update(updateData)
            .eq("id", aff.id);
        }
      }

      // Recharger les données d'affectations
      await reloadAffectations();

      // Calculer le total des jours de retard restants (pour mettre à jour frais_chantier)
      const { data: affectationsRetardRestantes } = await supabase
        .from("affectations_chantiers")
        .select("jours_travailles")
        .eq("chantier_id", chantierId)
        .like("notes", "Retard chantier%");

      const totalJoursRetardRestants = affectationsRetardRestantes?.reduce(
        (sum, aff) => sum + (aff.jours_travailles || 0), 0
      ) || 0;

      // Mettre à jour la description du frais de retard si elle existe
      if (totalJoursRetardRestants > 0) {
        // Chercher le frais de retard existant
        const { data: fraisRetard } = await supabase
          .from("frais_chantier")
          .select("id, description")
          .eq("chantier_id", chantierId)
          .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%')
          .limit(1)
          .maybeSingle();

        if (fraisRetard) {
          // Mettre à jour la description avec le nouveau nombre de jours
          // Extraire le format existant (X%/jour ou X€/jour ou paliers)
          const penaltyMatch = fraisRetard.description?.match(/\(([^)]+)\)$/);
          const penaltySuffix = penaltyMatch ? ` ${penaltyMatch[0]}` : '';
          const nouvelleDescription = `Retard de ${totalJoursRetardRestants} jour${totalJoursRetardRestants > 1 ? 's' : ''}${penaltySuffix}`;

          await supabase
            .from("frais_chantier")
            .update({ description: nouvelleDescription })
            .eq("id", fraisRetard.id);
        }
      } else {
        // Plus aucun jour de retard → supprimer le frais de retard
        await supabase
          .from("frais_chantier")
          .delete()
          .eq("chantier_id", chantierId)
          .or('type_frais.ilike.%Pénalité de retard%,type_frais.ilike.%Coût retard%');
      }

      // Forcer le recalcul des métriques via RPC (plus fiable que d'attendre le trigger)
      if (import.meta.env.DEV) console.log('[ChantierClosureDialog] Forcing metrics recompute via RPC...');
      const { data: newMetrics, error: rpcError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (rpcError) {
        console.error('[ChantierClosureDialog] Error computing metrics:', rpcError);
      } else if (newMetrics) {
        // Stocker les nouvelles métriques dans chantier_metrics_realtime
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierId,
            metrics: newMetrics,
            updated_at: new Date().toISOString()
          });
        setLocalMetrics(newMetrics);
        if (import.meta.env.DEV) console.log('[ChantierClosureDialog] Metrics updated:', newMetrics);
      }

      // Mettre la date de fin réelle à aujourd'hui (puisque les jours futurs sont supprimés)
      setDateFinReelle(aujourdhui);

      toast({
        title: "Jours futurs supprimés",
        description: `${joursFutursInfo.totalJours} jour(s) de travail supprimé(s).`,
      });

      // Passer à la checklist
      setStep('checklist');
    } catch (error: any) {
      console.error("Erreur suppression jours futurs:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les jours futurs: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsSupprimantJours(false);
    }
  };

  // Clôturer le chantier
  const handleClose = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("chantiers")
        .update({
          etat_chantier: 'termine',
          date_fin_reelle: dateFinReelle,
          cloture_avec_succes: true, // Marquer comme clôturé correctement via le flow
        })
        .eq("id", chantierId);

      if (error) throw error;

      // Invalider le cache React Query (alertes + chantier)
      if (entrepriseId) {
        invalidateAlertes(queryClient, entrepriseId);
        invalidateChantier(queryClient, entrepriseId, chantierId);
      }

      toast({
        title: "Chantier terminé",
        description: "Le chantier a été marqué comme terminé avec succès.",
      });

      // Passer à l'affichage du bilan
      setStep('bilan');
    } catch (error: any) {
      console.error("Erreur clôture chantier:", error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fermer le dialog
  const handleDialogClose = () => {
    const wasOnBilan = step === 'bilan';
    setStep('checklist');
    onOpenChange(false);
    if (wasOnBilan) {
      onClosed();
    }
  };

  // Retour à la checklist depuis une étape d'ajout
  const handleBackToChecklist = () => {
    setStep('checklist');
  };

  // Callback après ajout d'une facture
  const handleInvoiceUpdated = async () => {
    await reloadFactures();
    // Attendre un peu que la vue realtime se mette à jour
    await new Promise(resolve => setTimeout(resolve, 500));
    await reloadMetrics();
    toast({
      title: "Facture ajoutée",
      description: "La facture a été enregistrée. Vous pouvez en ajouter d'autres ou clôturer le chantier.",
    });
    setStep('checklist');
  };

  // Callback après ajout d'un frais
  const handleExpenseUpdated = async () => {
    await reloadFrais();
    // Attendre un peu que la vue realtime se mette à jour
    await new Promise(resolve => setTimeout(resolve, 500));
    await reloadMetrics();
    toast({
      title: "Coût ajouté",
      description: "Le coût a été enregistré. Vous pouvez en ajouter d'autres ou clôturer le chantier.",
    });
    setStep('checklist');
  };

  // Callback après ajout d'un paiement depuis le modal de clôture
  const handlePaymentSubmit = async (data: PaiementFormData) => {
    const result = await addPaiement(data);
    if (result) {
      toast({
        title: "Paiement enregistré",
        description: "Le paiement a été enregistré. Les montants ont été mis à jour.",
      });
      setStep('checklist');
    }
    return result;
  };

  // Navigation vers le simulateur de retard (ferme le modal et redirige)
  const handleGoToDelaySimulator = () => {
    // Stocker l'état de clôture pour permettre le retour
    startClosure(chantierId, dateFinReelle);
    onOpenChange(false);
    navigate(`/financial?tab=penalty-simulator&chantier=${chantierId}`);
  };

  // Rendu de l'étape jours futurs
  const renderJoursFuturs = () => {
    if (!joursFutursInfo) return null;

    return (
      <TerminerStepJoursFuturs
        chantierNom={chantierData.nom_chantier}
        joursFuturs={joursFutursInfo}
        onMaintenir={handleMaintenirJoursFuturs}
        onRetirer={handleRetirerJoursFuturs}
      />
    );
  };

  // Rendu de l'étape confirmation suppression
  const renderConfirmSuppression = () => {
    if (!comparaisonFinanciere) return null;

    return (
      <TerminerStepConfirmSuppression
        chantierNom={chantierData.nom_chantier}
        comparaison={comparaisonFinanciere}
        onAnnuler={handleAnnulerSuppression}
        onConfirmer={supprimerJoursFuturs}
        isLoading={isSupprimantJours}
      />
    );
  };

  // Rendu de l'étape checklist
  const renderChecklist = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-2xl">
          <CheckCircle2 className="h-6 w-6 text-green-500" />
          Terminer le chantier
        </DialogTitle>
        <DialogDescription>
          Vérifiez les informations avant de clôturer définitivement le chantier.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4">
        {/* Warning : paiements/contrats ST incomplets */}
        {((paiementTotals?.reste_a_encaisser ?? 0) > 0 || totalResteAFacturer > 0) && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                  Encaissements ou factures en attente
                </p>
                {(paiementTotals?.reste_a_encaisser ?? 0) > 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-300">
                    Il reste <strong>{paiementTotals!.reste_a_encaisser.toLocaleString('fr-FR')} € HT</strong> à encaisser côté client
                    ({paiementTotals!.pourcentage_encaisse?.toFixed(0) ?? 0}% encaissé)
                  </p>
                )}
                {totalResteAFacturer > 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-300">
                    Il reste <strong>{totalResteAFacturer.toLocaleString('fr-FR')} € HT</strong> de factures sous-traitant à recevoir
                    ({contratsST.filter(c => (c as any).reste_a_facturer > 0).length} contrat(s))
                  </p>
                )}
                <p className="text-xs text-amber-500/70 mt-1">
                  La clôture reste possible, mais ces montants resteront à suivre.
                </p>
              </div>
              {(paiementTotals?.reste_a_encaisser ?? 0) > 0 && (
                <Button
                  size="sm"
                  className="shrink-0 bg-primary hover:bg-primary/90 text-white font-medium rounded-full"
                  onClick={() => setStep('add-payment')}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un paiement
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Date de fin réelle */}
        <div className="space-y-2">
          <Label htmlFor="date-fin-reelle" className="flex items-center gap-2 text-base font-semibold">
            <Calendar className="h-5 w-5 text-primary" />
            Date de fin réelle
          </Label>
          <Input
            id="date-fin-reelle"
            type="date"
            value={dateFinReelle}
            onChange={(e) => setDateFinReelle(e.target.value)}
            className="max-w-xs"
          />
          <p className="text-sm text-muted-foreground">
            Date effective de fin du chantier (par défaut : dernier jour travaillé)
          </p>
        </div>

        {/* Section coûts résiduels */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-orange-500" />
            <h3 className="text-base font-semibold">Coûts non enregistrés ?</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Avant de clôturer, vérifiez qu'il ne reste pas de coûts à ajouter :
          </p>

          <div className="grid gap-3">
            {/* Bouton Retard - redirige vers le simulateur */}
            <Card
              className="cursor-pointer transition-all hover:border-orange-500/50 hover:bg-orange-500/5"
              onClick={handleGoToDelaySimulator}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <Clock className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium">Retard sur le chantier</p>
                    <p className="text-sm text-muted-foreground">Simuler et enregistrer les coûts de retard</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Ouvrir
                </Button>
              </CardContent>
            </Card>

            {/* Bouton Factures fournisseurs */}
            <Card
              className="cursor-pointer transition-all hover:border-blue-500/50 hover:bg-blue-500/5"
              onClick={() => setStep('add-invoice')}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Receipt className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">Factures fournisseurs</p>
                    <p className="text-sm text-muted-foreground">Scanner ou saisir une facture fournisseur</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardContent>
            </Card>

            {/* Bouton Coûts annexes */}
            <Card
              className="cursor-pointer transition-all hover:border-purple-500/50 hover:bg-purple-500/5"
              onClick={() => setStep('add-expense')}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Coins className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium">Coûts annexes</p>
                    <p className="text-sm text-muted-foreground">Location matériel, sous-traitance, divers...</p>
                  </div>
                </div>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section Heures Supplémentaires (si activé) */}
        {heuresSupSettings.heures_sup_actives && entrepriseId && (
          <div className="space-y-3">
            <HeuresSupRecap
              chantierId={chantierId}
              entrepriseId={entrepriseId}
              membresAffectes={membresAffectes}
              settings={heuresSupSettings}
            />
          </div>
        )}

        {/* Résumé du chantier */}
        <div className="rounded-lg border border-muted bg-muted/30 p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Résumé du chantier
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Nom :</span>{" "}
              <strong>{chantierData.nom_chantier}</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Client :</span>{" "}
              <strong>{chantierData.client}</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Budget :</span>{" "}
              <strong>{(localMetrics?.budget_ht || 0).toLocaleString()} € HT</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Marge actuelle :</span>{" "}
              <strong className={localMetrics?.marge_finale_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
                {(localMetrics?.marge_finale_pct || 0).toFixed(1)}%
              </strong>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={handleDialogClose} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
          Annuler
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]"
        >
          {loading ? "Traitement..." : "Tout est bon, clôturer"}
        </Button>
      </DialogFooter>
    </>
  );

  // Rendu de l'étape ajout facture
  const renderAddInvoice = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-xl">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 mr-1"
            onClick={handleBackToChecklist}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Receipt className="h-5 w-5 text-blue-500" />
          Ajouter une facture fournisseur
        </DialogTitle>
        <DialogDescription>
          Scannez ou saisissez manuellement une facture fournisseur
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 max-h-[60vh] overflow-y-auto">
        <InvoiceManager
          chantierId={chantierId}
          factures={localFactures}
          onUpdate={handleInvoiceUpdated}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleBackToChecklist} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la vérification
        </Button>
      </DialogFooter>
    </>
  );

  // Rendu de l'étape ajout coût annexe
  const renderAddExpense = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-xl">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 mr-1"
            onClick={handleBackToChecklist}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Coins className="h-5 w-5 text-purple-500" />
          Ajouter un coût annexe
        </DialogTitle>
        <DialogDescription>
          Scannez ou saisissez manuellement un coût annexe
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 max-h-[60vh] overflow-y-auto">
        <ExpensesManager
          chantierId={chantierId}
          frais={localFrais}
          onUpdate={handleExpenseUpdated}
          directSaveMode={true}
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleBackToChecklist} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la vérification
        </Button>
      </DialogFooter>
    </>
  );

  // Rendu de l'étape ajout paiement
  const renderAddPayment = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-xl">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 mr-1"
            onClick={handleBackToChecklist}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Coins className="h-5 w-5 text-amber-500" />
          Ajouter un paiement
        </DialogTitle>
        <DialogDescription>
          Enregistrez un paiement client reçu avant de clôturer
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 max-h-[60vh] overflow-y-auto">
        <PaiementFormContent
          onSubmit={handlePaymentSubmit}
          onCancel={handleBackToChecklist}
          facturesClients={facturesClients}
          totalDevis={paiementTotals?.total_devis ?? 0}
          resteAEncaisser={paiementTotals?.reste_a_encaisser ?? 0}
        />
      </div>
    </>
  );

  // Rendu de l'étape bilan
  const renderBilan = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-2xl">
          <FileText className="h-6 w-6 text-primary" />
          Bilan de clôture
        </DialogTitle>
        <DialogDescription>
          Voici le bilan complet du chantier terminé
        </DialogDescription>
      </DialogHeader>

      <ChantierBilan
        chantierData={chantierData}
        metrics={localMetrics}
        membres={membres}
        affectations={localAffectations}
        devis={devis}
        factures={localFactures}
        frais={localFrais}
        dateFinReelle={dateFinReelle}
        paiementTotals={paiementTotals}
      />

      <DialogFooter>
        <Button onClick={handleDialogClose} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
          Fermer
        </Button>
      </DialogFooter>
    </>
  );

  // Afficher un loader pendant le chargement des affectations
  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
      <p className="text-muted-foreground">Chargement des données...</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent variant="industrial" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {!affectationsLoaded && renderLoading()}
        {affectationsLoaded && step === 'jours_futurs' && renderJoursFuturs()}
        {affectationsLoaded && step === 'confirm_suppression' && renderConfirmSuppression()}
        {affectationsLoaded && step === 'checklist' && renderChecklist()}
        {affectationsLoaded && step === 'add-invoice' && renderAddInvoice()}
        {affectationsLoaded && step === 'add-expense' && renderAddExpense()}
        {affectationsLoaded && step === 'add-payment' && renderAddPayment()}
        {affectationsLoaded && step === 'bilan' && renderBilan()}
      </DialogContent>
    </Dialog>
  );
};

export default ChantierClosureDialog;
